// @ts-check
/** @typedef {import('@components/cards/businessview/shared/businessView.types').BusinessViewOverviewResponse} BusinessViewOverviewResponse */
import axios from 'axios';

import { useAuthenticatedQuery } from './useAuthenticatedQuery';

import { API_BASE_URL, FUNCTION_KEY, PROJECT_SCOPE } from '../config';
import logError from './metadata/ErrorLogging';

/**
 * Path of the DashboardAPI Business View overview operation, relative to
 * API_BASE_URL. Kept as a single exported helper so the route only has to be
 * changed in one place
 *
 * @param {string|number} projectId
 * @returns {string}
 */
export const buildOverviewPath = (projectId) => `project_workflows/${projectId}/overview`;

/**
 * Marker returned instead of a payload when the API reports that the project
 * has no Business View data. See the 404 handling below.
 */
const NO_DATA = { noData: true };

/**
 * Fetches the Business View overview (current summary, run trends and the
 * current workflow list) for a single project.
 * @function useBusinessViewOverview
 * @param {string|number|null|undefined} projectId
 *
 * @returns {{
 *   overview: BusinessViewOverviewResponse|null,
 *   hasNoData: boolean,
 *   isLoading: boolean,
 *   refetch: () => Promise<any>,
 *   error: any
 * }}
 */
export default function useBusinessViewOverview(projectId) {
  const fetchBusinessViewOverview = async (token) => {
    // The query is disabled without a project id, so this cannot run with one missing.
    // Fails here instead of requesting `project_workflows/undefined/overview`.
    if (!projectId) {
      throw new Error('useBusinessViewOverview was called without a project id.');
    }

    try {
      const response = await axios.get(`${API_BASE_URL}${buildOverviewPath(projectId)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-functions-key': FUNCTION_KEY,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      // isAxiosError rather than optional chaining: it narrows the caught value, and only a
      // real HTTP 404 should be read as "no Business View data".
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return NO_DATA;
      }

      logError('Failed to load Business View overview in useBusinessViewOverview', error, { project_id: projectId });
      throw error;
    }
  };

  const projectScope = [PROJECT_SCOPE];

  // Project ID is the only thing that affects the result, so it is the only key part.
  const queryKey = ['businessViewOverview', projectId];

  const isEnabled = !!projectId;

  const {
    data = null,
    isLoading,
    refetch,
    error,
  } = useAuthenticatedQuery(queryKey, fetchBusinessViewOverview, projectScope, {
    enabled: isEnabled,
    staleTime: 60 * 1000, // Data will be cached for 1 minute
  });

  const hasNoData = data === NO_DATA;

  return {
    overview: hasNoData ? null : data,
    hasNoData,
    isLoading: isEnabled && isLoading,
    refetch,
    error,
  };
}
